import * as ngCore from '@angular/core'
import * as ngRouter from '@angular/router'
import * as rxjs from 'rxjs'

import { RouteParam } from '../../../route-param'
import * as me from '../../me'

import * as models from './models'

const ROUTE_SCOPE_KEY = 'adminRouteScope'
const ROUTE_SCOPES: ReadonlySet<unknown> = new Set<unknown>(
  Object.values(models.RouteAccess.Scope),
)

/**
 * Route metadata used by the Admin Center guard.
 */
export const RouteData = {
  adminCenter: { [ROUTE_SCOPE_KEY]: models.RouteAccess.Scope.AdminCenter },
  app: { [ROUTE_SCOPE_KEY]: models.RouteAccess.Scope.App },
  platformKnowledgeBases: {
    [ROUTE_SCOPE_KEY]: models.RouteAccess.Scope.PlatformKnowledgeBases,
  },
  platformUsers: { [ROUTE_SCOPE_KEY]: models.RouteAccess.Scope.PlatformUsers },
  scenario: { [ROUTE_SCOPE_KEY]: models.RouteAccess.Scope.Scenario },
  tenant: { [ROUTE_SCOPE_KEY]: models.RouteAccess.Scope.Tenant },
  zone: { [ROUTE_SCOPE_KEY]: models.RouteAccess.Scope.Zone },
} as const satisfies Record<string, ngRouter.Data>

/**
 * Guards Admin Center routes after the current user's access tree has loaded.
 */
export const canActivateAdminRoute: ngRouter.CanActivateFn = route => {
  const meFeature = ngCore.inject(me.Feature)
  const router = ngCore.inject(ngRouter.Router)
  const accessRoute = getRouteAccess(route)

  return rxjs
    .combineLatest([
      meFeature.selectors.currentUser.asObservable,
      meFeature.selectors.isLoading.asObservable,
    ])
    .pipe(
      rxjs.filter(([, isLoading]) => isLoading !== true),
      rxjs.take(1),
      rxjs.switchMap(([user]) => {
        if (!models.RouteAccess.needsAppLookup(user, accessRoute)) {
          return rxjs.of(
            models.RouteAccess.canAccess(user, accessRoute)
              ? true
              : router.createUrlTree(['/']),
          )
        }

        const { tenantId, zoneId } = accessRoute
        if (!tenantId || !zoneId) {
          return rxjs.of(router.createUrlTree(['/']))
        }

        return meFeature.zoneApps.selectors
          .getShouldRequest(zoneId)
          .asObservable.pipe(
            rxjs.take(1),
            rxjs.switchMap(shouldRequest => {
              if (shouldRequest) {
                meFeature.zoneApps.actions.get
                  .from('AdminRouteGuard')
                  .dispatch({ tenantId, zoneId })
              }
              return meFeature.zoneApps.selectors
                .getResult(zoneId)
                .asObservable.pipe(
                  rxjs.filter(({ isSettled }) => isSettled),
                  rxjs.take(1),
                  rxjs.map(({ apps }) =>
                    models.RouteAccess.canAccess(user, accessRoute, apps)
                      ? true
                      : router.createUrlTree(['/']),
                  ),
                )
            }),
          )
      }),
    )
}

/**
 * Converts the Angular route snapshot into the guard's route access model.
 */
function getRouteAccess(
  route: ngRouter.ActivatedRouteSnapshot,
): models.RouteAccess {
  return {
    scope: getRouteScope(route),
    tenantId: getRouteParam(route, RouteParam.TenantId),
    zoneId: getRouteParam(route, RouteParam.ZoneId),
    appId: getRouteParam(route, RouteParam.AppId),
    scenarioId: getRouteParam(route, RouteParam.ScenarioId),
  }
}

/**
 * Reads and validates the route scope used by the admin guard.
 */
function getRouteScope(
  route: ngRouter.ActivatedRouteSnapshot,
): models.RouteAccess.Scope | undefined {
  const scope: unknown = route.data[ROUTE_SCOPE_KEY]
  return ROUTE_SCOPES.has(scope)
    ? (scope as models.RouteAccess.Scope)
    : undefined
}

/**
 * Gets a path parameter from the current admin route.
 */
function getRouteParam(
  route: ngRouter.ActivatedRouteSnapshot,
  param: RouteParam,
): string | undefined {
  return route.paramMap.get(param) ?? undefined
}
